import { Grid, Heading, InputOnFocus } from '@afterpaytouch/core'
import { useRouter } from 'next/router'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, useEffect, useState } from 'react'
import FormBuilder from '@afterpaytouch/form'
import { Channel, IdentityErrorCode, IdentityDocumentVerificationStatus, IdentityMedicareColour } from '@afterpaytouch/portal-api/identity'
import { getIdentityVerifyErrorCode } from '@afterpaytouch/portal-api/identity/utils'
import { DisabledModal } from '../DisabledModal'
import { FormWrapper } from '../FormWrapper'
import { ErrorMessage } from '../VerifyIdentity/components'
import { DocumentProps, SupportedLocale } from '../../model'
import { useConsumerSelector, useIdentityMedicareMutation } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { formatNumberForBackend, getMedicareSchema, MedicareForm } from './utils'
import { TrackingEvent } from '../../model/monitoring'
import { isDifferentWithProfileDOB, isDifferentWithProfileNames, useSetErrorsAndLogEvent } from '../VerifyIdentity/helper'
import { compareObjects } from '../../utils/comparison'
import { USER_VERIFICATION_ERRORS } from '../VerifyIdentity/constants'

interface SchemaUpdates {
  selectedColour?: IdentityMedicareColour
  showProfileNameAlert: boolean
  showProfileDobAlert: boolean
}

interface SubmittedRequest extends MedicareForm {
  channel: Channel
  number: string
}

const terminalErrors = new Set<IdentityErrorCode>([IdentityErrorCode.MaxAttemptsReached, IdentityErrorCode.MaxAttemptsReachedForType])

export const Medicare: FunctionComponent<DocumentProps> = ({ onSuccess = () => {}, onFailure = () => {} }) => {
  const [lock, setLock] = useState(false)
  const [schemaUpdates, setSchemaUpdates] = useState<SchemaUpdates>({
    showProfileNameAlert: false,
    showProfileDobAlert: false,
  })
  const [error, setError] = useState<IdentityErrorCode | null>(null)
  const { t } = useTranslation()
  const { profile } = useConsumerSelector()
  const { headingSize, buttonSize } = useCheckoutSizes()
  const router = useRouter()
  const [identityVerifyMedicare] = useIdentityMedicareMutation()
  const [submittedRequest, setSubmittedRequest] = useState<SubmittedRequest>()
  const setErrorsAndLogEvent = useSetErrorsAndLogEvent(setError, setLock, TrackingEvent.MEDICARE_SUBMIT_FAILED)

  useEffect(() => {
    if (error !== null && terminalErrors.has(error)) {
      onFailure()
    }

    if (error === null || !terminalErrors.has(error)) {
      // Scroll to top
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  }, [error, onFailure])

  const profileDateOfBirth = profile?.dateOfBirth ?? ''

  const handleSubmit = async (data: MedicareForm): Promise<void> => {
    try {
      setErrorsAndLogEvent({ lock: true, event: TrackingEvent.PRESSED_HARDID_MEDICARE_PAGE_VERIFY_BUTTON })

      const request = {
        ...data,
        channel: Channel.CHECKOUT,
        number: formatNumberForBackend(data.number),
      }

      if (compareObjects(request, submittedRequest)) {
        setErrorsAndLogEvent({ error: IdentityErrorCode.IdentityAlreadyRejected, lock: false })
        return
      }

      const response = await identityVerifyMedicare(request).unwrap()

      if (response.status === IdentityDocumentVerificationStatus.VERIFIED) {
        onSuccess()
      } else if (response.status === IdentityDocumentVerificationStatus.REJECTED) {
        setSubmittedRequest(request)
        setErrorsAndLogEvent({ error: IdentityErrorCode.IdentityCheckRejected, lock: false })
      }
    } catch (e) {
      setErrorsAndLogEvent({ error: getIdentityVerifyErrorCode(e) ?? IdentityErrorCode.Default, lock: false })
    }
  }

  const onNameBlur = (ev: InputOnFocus): void => {
    setSchemaUpdates({
      ...schemaUpdates,
      showProfileNameAlert: isDifferentWithProfileNames(ev.target.value, profile),
    })
  }

  const handleOnChange = (data: MedicareForm): void => {
    setError(null)
    setSchemaUpdates({
      ...schemaUpdates,
      selectedColour: data.colour,
      showProfileDobAlert: isDifferentWithProfileDOB(data.dateOfBirth, profileDateOfBirth),
    })
  }

  return (
    <>
      <Grid>
        <Heading size={headingSize}>{t('verifyIdentity:document:medicare')}</Heading>
      </Grid>
      <ErrorMessage identityType='medicare' error={error} testNameSpace={'verify-identity-medicare-error'} />
      <FormWrapper>
        <FormBuilder
          disabled={lock}
          schema={getMedicareSchema({
            t,
            buttonSize,
            submitted: lock,
            profileDateOfBirth,
            onNameBlur,
            locale: router.locale as SupportedLocale,
            error: USER_VERIFICATION_ERRORS.includes(error as IdentityErrorCode),
            ...schemaUpdates,
          })}
          testNameSpace={'verify-identity-medicare'}
          onSubmit={handleSubmit}
          onChange={handleOnChange}
        />
      </FormWrapper>
      {lock && <DisabledModal />}
    </>
  )
}
